import { Component, Inject, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { ToolboxTalkModel } from 'src/app/models/toolbox-talk.model';
import { SitesSelectorComponent } from 'src/app/shared/sites-selector/sites-selector.component';
import { UtilsService } from '../../shared/utils.service';
import { FilesRemoveComponent } from 'src/app/files/files-remove/files-remove.component';
import { FileService } from 'src/app/shared/file.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToolboxTalksEditDialogComponent } from '../toolbox-talks-edit-dialog/toolbox-talks-edit-dialog.component';
import {
  NetworkedUsersSelectorComponent
} from 'src/app/shared/networked-users-selector/networked-users-selector.component';
import { UserModel } from 'src/app/models/user.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { ToolboxTalksTemplatesSnippetsSelectorComponent } from '../toolbox-talks-templates-snippets-selector/toolbox-talks-templates-snippets-selector.component';
import { ToolboxTalkTemplateModel } from '../../models/toolbox-talk-template.model';
import { RBACService } from '../../shared/rbac.service';

@Component({
    selector: 'app-toolbox-talks-edit',
    templateUrl: './toolbox-talks-edit.component.html',
    styleUrls: ['./toolbox-talks-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ToolboxTalksEditComponent implements OnInit {

  toolbox_talk: ToolboxTalkModel = new ToolboxTalkModel();

  newFiles: FileList;

  selectedFiles: File[] = [];

  inProgress: boolean = false;

  assignee: UserModel = {} as UserModel;

  site_id: number = 0;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public router: Router,
    public renderer: Renderer2,
    public utils: UtilsService,
    private fileService: FileService,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private dialog: MatDialogRef<ToolboxTalksEditComponent | ToolboxTalksEditDialogComponent>,
    private rbac: RBACService
  ) {
  }

  ngOnInit() {
    if (this.dialogData.id && (typeof this.dialogData.id == 'number')) {
      this.find();
    } else {
      // If the toolbox talk data is present, the "Create From" button was pressed.
      if( this.dialogData.toolbox_talk ) {
        this.toolbox_talk.apply(this.dialogData.toolbox_talk);
      }

      // Store the site id for all new toolbox talks.
      this.toolbox_talk.site_id = this.dialogData.site_id; // The site id must always be present.
    }
  }

  onRemoveFiles() {
    this.utils.showComponentDialog(
      FilesRemoveComponent,
      this.toolbox_talk.files,
      {
        width: '700px'
      },
      () => this.find()
    );
  }

  async onSubmit(form: NgForm, continueWhenDone: boolean = false): Promise<void> {
    // make sure the form data is valid
    if ( form.valid ) {

      if ( this.inProgress ) {
        return;
      }

      this.inProgress = true;

      // Only set the site id if this is a new toolbox talk.
      if ( !this.toolbox_talk.id ) {
        this.toolbox_talk.site_id = this.dialogData.site_id ? this.dialogData.site_id : 0;
      }

      this.toolbox_talk.agenda = this.toolbox_talk.agenda.replace(/><li[^>]*>/g, ">\n<li>")

      // check if the site should be created or updated
      if (this.toolbox_talk.id) {
        this.update(this.toolbox_talk, continueWhenDone);
      } else {
        this.create(this.toolbox_talk);
      }
    } else {
      alert('Please make sure you enter all fields with valid information.');
    }
  }

  onSelectSites() {
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: this.toolbox_talk.selected_site_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.toolbox_talk.selected_site_ids = results;
        }
      }
    );
  }

  /**
   * Make a user selection for Toolbox Talk attendees.
   */
  onSelectUsers() {
    this.utils.showComponentDialog(NetworkedUsersSelectorComponent, {
      selected: this.toolbox_talk.user_ids,
      multiple: true,
      visitor_site_id: this.toolbox_talk.site_id
    }, {
      width: '1200px'
    }, (results: number[]) => {
      if (!results) { return; }
      this.toolbox_talk.user_ids = results;
    });
  }

  /**
   * Make a user selection for Toolbox Talk reported by users.
   */
  onSelectReportedBy() {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        multiple: false,
        selected: this.toolbox_talk.reported_by ? [this.toolbox_talk.reported_by] : [],
        visitor_site_id: this.toolbox_talk.site_id
      },
      {
        width: '1200px'
      },
      (userId: number) => {
        if (!userId) { return; }
        this.toolbox_talk.reported_by = userId;
      }
    );
  }

  onSelectSnippets() {
    this.utils.showComponentDialog(
      ToolboxTalksTemplatesSnippetsSelectorComponent,
      { multiple: true },
      {
        width: '1200px'
      },
      (selectedIds: number[]) => {
        if (Array.isArray(selectedIds) && selectedIds.length > 0) {
          this.api.makeRequest(
            'get',
            `v1/toolbox-talks-templates/${selectedIds.join(',')}`
          )
            .then((response: any) => {
              const templates = response.data as ToolboxTalkTemplateModel[];
              this.appendToAgenda(templates);
            })
            .catch((err) => this.utils.handleAPIErrors(err));
        }
      }
    );
  }

  appendToAgenda(templates: ToolboxTalkTemplateModel[]) {
    const formattedAgendas = templates
      .filter(t => t.agenda?.trim())
      .map(t => `<h3>${t.name}</h3>\n${t.agenda.trim()}`);

    if (formattedAgendas.length === 0) {
      return
    }

    const mergedAgenda = formattedAgendas.join('\n\n');

    if (!this.toolbox_talk.agenda) {
      this.toolbox_talk.agenda = mergedAgenda;
    } else {
      this.toolbox_talk.agenda += `\n\n${mergedAgenda}`;
    }
  }

  onRemove() {
    this.utils.showModal(
      'Remove ' + this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk'),
      'Are you sure you want to remove this ' + this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') + '?',
      () => {
        this.api.makeRequest(
          'delete',
          'v1/toolbox-talks/' + this.toolbox_talk.id
        ).then((response: any) => {
          this.utils.showToast('The ' + this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') + ' was removed');
          this.dialog.close();
        }).catch((err: any) => {
          this.utils.showModal('Error', err.error.message);
        });
      }
    );
  }

  private find() {
    this.api.makeRequest(
      'get',
      'v1/toolbox-talks/' + this.dialogData.id
    ).then((response: any) => {
      this.toolbox_talk.apply(response.data); // Apply the data instead of overriding the object.
      this.toolbox_talk.user_ids = this.toolbox_talk.users.map(
        (user) => user.id
      );
    }).catch((err: any) => {
      this.utils.showModal('Error', err.error.message);
    });
  }

  /**
   * Creates a new Toolbox Talk using the provided data and handles the response or error accordingly.
   *
   * @param {ToolboxTalkModel} toolboxTalk - The model containing the data for the Toolbox Talk to be created.
   * @param {Function} [callback] - An optional callback function to be executed after the creation process completes.
   * @return {void} No return value.
   */
  private create(toolboxTalk: ToolboxTalkModel, callback?: any): void {
    this.api.makeRequest(
      'post',
      'v1/toolbox-talks',
      toolboxTalk
    ).then((response: any) => {
      this.utils.showToast('The ' + this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') + ' was created.');
      this.toolbox_talk.id = response.data.id;
      this.dialogData.id = response.data.id;

      // Determine if user has edit permission for this context.
      const editFeature = this.toolbox_talk.site_id
        ? RBACService.RBAC_F.F_SITES_TOOLBOX_TALKS
        : RBACService.RBAC_F.F_REGISTERS_TOOLBOX_TALKS;
      const canEdit = this.rbac.userCanAccess([
        RBACService.getPermission(editFeature, RBACService.RBAC_A.A_EDIT)
      ], true);

      this.uploadSelectedFiles(this.toolbox_talk.id, (): void => {
        this.inProgress = false;
        if (typeof callback === 'function') {
          callback();
        }
        this.dialog.close(true);
      });

      // Auto-close if no files to upload and user lacks edit permission.
      if (this.selectedFiles.length === 0) {
        this.inProgress = false;
        if (!canEdit) {
          this.dialog.close(true);
        } else {
          // Transition to edit mode. Reload the record.
          this.find();
        }
      }
    }).catch((err: any) => {
      this.utils.showModal('Error', err.error.message);
      this.inProgress = false;
    });
  }

  private update(toolboxTalk: ToolboxTalkModel, continueWhenDone: boolean = false) {
    this.api.makeRequest(
      'put',
      'v1/toolbox-talks/' + toolboxTalk.id,
      toolboxTalk
    ).then((response: any) => {
      this.utils.showToast('The ' + this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') + ' was updated.');
      this.inProgress = false;
      this.newFiles = null;
      if(continueWhenDone) {
        this.dialog.close();
        if(this.dialogData.redirect_url && this.dialogData.redirect_url != '') {
          this.router.navigate([this.dialogData.redirect_url]);
        } else {
          this.router.navigate([`/toolbox-talks/${toolboxTalk.id}/view/`]);
        }
      } else {
        this.find();
      }
    }).catch((err: any) => {
      this.utils.showModal('Error', err.error.message);
      this.inProgress = false;
    });
  }

  /**
   * Handles the event of file selection by processing the provided FileList
   * and adding the selected files to the internal storage.
   *
   * @param {FileList} fileList - A list of files selected by the user.
   * @return {void} Does not return a value.
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Uploads the selected files to the server for the specified identifier.
   *
   * @param {number} id - The identifier to associate the uploaded files with.
   * @param {Function} [callback] - An optional callback function to be executed once the upload process is completed.
   * @return {void}
   */
  private uploadSelectedFiles(id: number, callback?: any): void {
    if ( id && this.selectedFiles.length > 0 ) {
      this.api.makeUploadRequest(`v2/file-manager/toolbox-talks/${id}`, this.selectedFiles)
        .then((response: any): void => {
          this.utils.showToast('Your files successfully uploaded.');
        })
        .finally((): void => {
          this.selectedFiles.length = 0;
          if ( typeof callback === 'function' ) {
            callback();
          }
        });
    }
  }

  formatUsers(users: UserModel[]): string {
    return users.map((user) => user.contact_person).join(', ');
  }

  onExport(type: string = 'pdf') {
    if ( !this.toolbox_talk.id || !this.toolbox_talk.site_id ) {
      this.utils.showModal('Error Message', 'We are unable to export the ' + this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') + '. Please try again or contact us at ' + this.utils.getSupportEmailAddress() + ' for assistance.');
      return;
    }

    this.api.makeDownloadRequest(`v1/toolbox-talks/export/${type}/` + this.toolbox_talk.id, {}, {
      site_ids: [this.toolbox_talk.site_id]
    })
    .then((response) => {
      // handle internet explorer - No longer supported
      // if ( window.navigator && window.navigator.msSaveOrOpenBlob ) {
      //   window.navigator.msSaveOrOpenBlob(response);
      //   return;
      // }

      // Get the current date object
      const date = new Date();

      // Create object url to handle file downloads
      const data = window.URL.createObjectURL(response);

      // Create a download link
      const downloadLink = document.createElement('a');
      downloadLink.href = data;
      downloadLink.download = (this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks')) + ` - ${date.getFullYear()}${(date.getMonth()+1)}${date.getDate()}.${type}`;
      // Initiate the download
      downloadLink.click();

      // For Firefox it is necessary to delay revoking the ObjectURL
      setTimeout(function() {
        window.URL.revokeObjectURL(data);
      }, 300); // Minimum 300 miliseconds
    })
    .catch((errorResponse) => {
      this.utils.showModal('Error', errorResponse.message);
    });
  }

}
